/**
 * HTTP client for the NS Sales Bot backend.
 *
 * Split the way the backend is split, and for the same reason:
 *
 *   assessment*  the CLIENT surface. Everything is addressed by the access
 *                token from the assessment link. There is deliberately no
 *                function here for a report, an internal state or a
 *                document's text — a client has no business with any of them,
 *                and the backend would refuse anyway. Their own conversation
 *                is the exception, and it comes back stripped to who spoke,
 *                what was said and when.
 *
 *   internal*    the INTERNAL surface. Addressed by session id, which only
 *                reaches the browser on internal pages.
 *
 * Uses the platform `fetch` API — no HTTP library dependency is needed.
 */

import { API_URL } from '../config';

/** Error carrying the HTTP status of a failed backend call. */
export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Perform a GET request against the backend and parse the JSON response.
 *
 * @param {string} path Path beginning with a slash, e.g. `/health`.
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<unknown>}
 */
export async function get(path, { timeoutMs = 5000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(payload?.message || payload?.detail || `Request to ${path} failed`, response.status);
    }
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function post(path, body, { timeoutMs = 15000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const message = payload?.message || payload?.detail || `Request to ${path} failed`;
      throw new ApiError(message, response.status);
    }
    return payload;
  } finally {
    clearTimeout(timer);
  }
}

async function postFile(path, file, { timeoutMs = 600000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const body = new FormData();
  body.append('file', file, file.name);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
      body,
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new ApiError(payload?.message || payload?.detail || 'The document could not be uploaded', response.status);
    }
    return payload;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch backend health.
 *
 * @returns {Promise<{status: string, service: string, version: string, storage: object}>}
 */
export function fetchHealth() {
  return get('/health');
}

// ── Authentication (Keycloak OIDC) ────────────────────────────────

/** Get logged-in NS user profile and status. */
export function fetchAuthStatus() {
  return get('/api/auth/me');
}

/** Fetch Keycloak login URL from backend. */
export function fetchLoginUrl() {
  return get('/api/auth/login');
}

/** Logout current session and return Keycloak logout URL. */
export function logoutUser() {
  return post('/api/auth/logout');
}

// ── NS Business Assessment: the client surface ──────────────────
//
// One credential, one assessment. Every call below is addressed by the access
// token in the client's link; none of them names a session.

/**
 * Read what the client's page should show for this link.
 *
 * @param {string} token
 * @returns {Promise<{state: string, complete: boolean, title: string,
 *                    assistant_name: string, instructions: string,
 *                    completion_title: string, completion_message: string,
 *                    documents_enabled: boolean, accepted_extensions: string[],
 *                    max_document_bytes: number}>}
 */
export function fetchAssessment(token) {
  return get(`/api/business-assessment/${token}`);
}

/**
 * Poll whether the assessment has finished.
 *
 * The client's only view of the internal engine: whether it is over. Never
 * which stage, never what was learned.
 *
 * @param {string} token
 * @returns {Promise<{state: string, complete: boolean}>}
 */
export function fetchAssessmentStatus(token) {
  return get(`/api/business-assessment/${token}/status`);
}

/**
 * Read what has already been said in this assessment.
 *
 * An assessment can span several sittings, and the page has no memory of its
 * own: the stored conversation is what lets a returning client see what they
 * are continuing. Roles come back named as the feed names them, and each turn
 * carries nothing but who spoke, what was said and when.
 *
 * @param {string} token
 * @returns {Promise<{count: number, turns: Array<{role: string, text: string, timestamp: string}>}>}
 */
export function fetchAssessmentTranscript(token) {
  return get(`/api/business-assessment/${token}/transcript`, { timeoutMs: 15000 });
}

/**
 * Begin or rejoin the assessment and get credentials for its LiveKit room.
 *
 * The response carries a short-lived token scoped to one room and one
 * identity. It never contains the LiveKit API key or secret, and it does not
 * name the session.
 *
 * A conversation that has not begun yet must carry the client's name and
 * organisation: the backend refuses to issue a room token without them, which
 * is what keeps the voice session from starting before they are known.
 *
 * @param {string} token
 * @param {{client_name: string, organization_name: string}} [identity]
 * @returns {Promise<{livekit_url: string, token: string, token_expires_in: number,
 *                    participant_identity: string}>}
 */
export function startAssessment(token, identity) {
  return post(`/api/business-assessment/${token}/start`, identity);
}

/**
 * Tell the backend the client has left the room.
 *
 * The ordinary exit, carrying no intent: a refresh, a closed tab, a dropped
 * connection. The backend decides what it means for the lifecycle — an
 * assessment that actually closed is completed, one that did not is
 * incomplete and still resumable from the same link. Idempotent.
 *
 * @param {string} token
 */
export function leaveAssessment(token) {
  return post(`/api/business-assessment/${token}/leave`);
}

/**
 * Tell the backend the client chose to finish the conversation.
 *
 * The explicit signal, sent only when the client presses End Conversation.
 * A page that is being torn down must never send this — it goes to
 * `leaveAssessment` instead, which keeps the conversation resumable.
 *
 * It does not complete the assessment: the backend still decides that from
 * what the conversation actually covered. Idempotent.
 *
 * @param {string} token
 */
export function finishAssessment(token) {
  return post(`/api/business-assessment/${token}/finish`);
}

/**
 * List the documents shared with this assessment.
 *
 * Filenames and sizes only — the client never sees extracted text, evidence,
 * or anything the internal engine made of a document.
 *
 * @param {string} token
 */
export function fetchAssessmentDocuments(token) {
  return get(`/api/business-assessment/${token}/documents`);
}

/**
 * Share one document with this assessment.
 *
 * @param {string} token
 * @param {File} file
 */
export function uploadAssessmentDocument(token, file) {
  return postFile(`/api/business-assessment/${token}/documents`, file);
}

// ── Internal surface ────────────────────────────────────────────
//
// Addressed by session id. Reached only from internal pages; a client holds
// no session id, so none of this is callable from an assessment link.

/** Create an assessment and get the link to send a client. */
export function createAssessment() {
  return post('/api/internal/sessions');
}

/** List every assessment on this machine. */
export function fetchSessions() {
  return get('/api/internal/sessions', { timeoutMs: 15000 });
}

/** Read one session's internal record. */
export function fetchSession(sessionId) {
  return get(`/api/internal/sessions/${sessionId}`);
}

/**
 * Delete a session and everything stored for it — its PostgreSQL rows and its
 * MinIO objects. Resolves on 204; rejects with an ApiError otherwise (404 when
 * there is no such session, 503 when storage could not complete it, in which
 * case the session is left in place).
 */
export async function deleteSession(sessionId, { timeoutMs = 30000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const path = `/api/internal/sessions/${encodeURIComponent(sessionId)}`;

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(
        payload?.message || payload?.detail || 'The session could not be deleted',
        response.status,
      );
    }
  } finally {
    clearTimeout(timer);
  }
}

/** Read the L1-L8 discovery state for a session. */
export function fetchDiscovery(sessionId) {
  return get(`/api/internal/sessions/${sessionId}/discovery`);
}

/** Read the stored transcript for a session. */
export function fetchTranscript(sessionId) {
  return get(`/api/internal/sessions/${sessionId}/transcript`, { timeoutMs: 20000 });
}

/** Read a stored sales discovery report. */
export function fetchReport(sessionId) {
  return get(`/api/internal/sessions/${sessionId}/report`, { timeoutMs: 20000 });
}

/**
 * Run finalisation for a session by hand.
 *
 * Internal repair only. Assessments finalise themselves when the voice agent
 * leaves the room; this exists for the case where that did not happen, and it
 * takes the same idempotent path.
 *
 * @param {string} sessionId
 */
export function finalizeSession(sessionId) {
  return post(`/api/internal/sessions/${sessionId}/finalize`, undefined, { timeoutMs: 180000 });
}

/** Read the document evidence raised during an interview. */
export function fetchDocumentEvidence(sessionId) {
  return get(`/api/internal/sessions/${sessionId}/document-evidence`);
}

/** List the documents attached to a session, with extraction detail. */
export function fetchDocuments(sessionId) {
  return get(`/api/internal/sessions/${sessionId}/documents`);
}

/**
 * Attach one document to a session from an internal page.
 *
 * The same storage path as a client upload; the internal caller simply names
 * the session directly instead of holding an access token.
 *
 * @param {string} sessionId
 * @param {File} file
 */
export function uploadDocument(sessionId, file) {
  return postFile(`/api/internal/sessions/${sessionId}/documents`, file);
}

/** Absolute URL for downloading an attached document. */
export function documentDownloadUrl(sessionId, documentId) {
  return `${API_URL}/api/internal/sessions/${sessionId}/documents/${documentId}/download`;
}

/** Absolute URL of a session's recording. */
export function sessionAudioUrl(sessionId) {
  return `${API_URL}/api/internal/sessions/${sessionId}/audio`;
}

/** Absolute URL for downloading the Sales Discovery Report as a Word DOCX file. */
export function reportDocxDownloadUrl(sessionId) {
  return `${API_URL}/api/internal/sessions/${sessionId}/report/docx`;
}
